import { useEffect, useState } from "react";

import type { MealType, RecipeSuggestion } from "@sytofood/shared";

import { type LoggedMeal, type ParseMealView, api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { pantryCopy } from "../lib/i18n/pantry";
import { normalizeToPantryUnit } from "../lib/unit-normalize";
import { ScreenShell } from "./screen-shell";
import {
  AiFooter,
  Chip,
  Conf,
  Eyebrow,
  H1,
  MonoMeta,
  TopBar,
  confLevelFromScore,
} from "./ui/primitives";

type Props = {
  householdId: string;
  onLogged: (meal: LoggedMeal) => void;
  onUseAsMeal: (parsed: ParseMealView) => void;
  onBack: () => void;
};

export function RecipesScreen({ householdId, onLogged, onUseAsMeal, onBack }: Props) {
  const { lang } = useLang();
  const t = pantryCopy[lang];
  const MEAL_TYPE_LABELS = t.mealType;
  const [recipes, setRecipes] = useState<RecipeSuggestion[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mealTypeFilter, setMealTypeFilter] = useState<MealType | "all">("all");
  const [loggingIdx, setLoggingIdx] = useState<number | null>(null);
  const [addingShopIdx, setAddingShopIdx] = useState<number | null>(null);
  const [addedShopIdx, setAddedShopIdx] = useState<Set<number>>(new Set());
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  async function load(mealType?: MealType) {
    setLoading(true);
    setError(null);
    setRecipes(null);
    setAddedShopIdx(new Set());
    setExpandedIdx(null);
    try {
      const res = await api.suggestRecipes(householdId, mealType ? { mealType } : {});
      setRecipes(res.recipes);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.recipesErrGenerate);
    } finally {
      setLoading(false);
    }
  }

  async function addMissingToShopping(recipe: RecipeSuggestion, idx: number) {
    if (recipe.missing.length === 0) return;
    setAddingShopIdx(idx);
    setError(null);
    try {
      await api.bulkCreateShopping(householdId, {
        items: recipe.missing.map((ing) => ({
          name: ing.name,
          quantity: ing.quantity,
          unit: normalizeToPantryUnit(ing.unit),
          source: "recipe",
        })),
      });
      setAddedShopIdx((prev) => new Set(prev).add(idx));
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errAddToList);
    } finally {
      setAddingShopIdx(null);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: initial load only.
  useEffect(() => {
    load();
  }, []);

  function changeFilter(next: MealType | "all") {
    setMealTypeFilter(next);
    load(next === "all" ? undefined : next);
  }

  async function quickLog(recipe: RecipeSuggestion, idx: number) {
    setLoggingIdx(idx);
    setError(null);
    try {
      const { meals } = await api.saveMeals(
        {
          items: [
            {
              name: recipe.name,
              kcal: recipe.estimatedKcal,
              proteinG: recipe.estimatedProteinG,
              fatG: recipe.estimatedFatG,
              carbsG: recipe.estimatedCarbsG,
              portionGrams: null,
              confidence: recipe.confidence,
            },
          ],
          source: "manual_ai",
          ...(recipe.mealType ? { mealType: recipe.mealType } : {}),
        },
        householdId,
      );
      const meal = meals[0];
      if (meal) onLogged(meal);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errLog);
    } finally {
      setLoggingIdx(null);
    }
  }

  function editBeforeLog(recipe: RecipeSuggestion) {
    onUseAsMeal({
      items: [
        {
          name: recipe.name,
          kcal: recipe.estimatedKcal,
          proteinG: recipe.estimatedProteinG,
          fatG: recipe.estimatedFatG,
          carbsG: recipe.estimatedCarbsG,
          portionGrams: null,
          confidence: recipe.confidence,
        },
      ],
      source: "manual_ai",
      rawInputText: `Z lodówki: ${recipe.name}`,
    });
  }

  const count = recipes?.length ?? 0;

  return (
    <ScreenShell hideHeader withBottomNav>
      <div style={{ marginBottom: 18 }}>
        <TopBar back={t.recipesBack} title={t.recipesTopTitle} onBack={onBack} />
      </div>

      <H1 accent=".">{t.recipesTitle}</H1>
      <p style={{ marginTop: 12, fontSize: 14, color: "var(--ink-2)" }}>
        <span
          className="tabular"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 800,
            color: "var(--ink)",
          }}
        >
          {t.recipesCount(count)}
        </span>
        {t.recipesBudgetNote}
      </p>

      {/* Meal type filter chips */}
      <div style={{ marginTop: 18, display: "flex", gap: 6, flexWrap: "wrap" }}>
        <Chip
          active={mealTypeFilter === "all"}
          onClick={() => changeFilter("all")}
          disabled={loading}
        >
          {t.recipesFilterAll}
        </Chip>
        {(["breakfast", "lunch", "dinner", "snack"] as MealType[]).map((t) => (
          <Chip
            key={t}
            active={mealTypeFilter === t}
            onClick={() => changeFilter(t)}
            disabled={loading}
          >
            {MEAL_TYPE_LABELS[t]}
          </Chip>
        ))}
      </div>

      {error ? (
        <p
          className="text-[12px] px-3 py-2 mt-3 rounded-md"
          style={{ background: "var(--bad-bg)", color: "var(--bad-fg)" }}
        >
          {error}
        </p>
      ) : null}

      {loading ? (
        <p style={{ marginTop: 24, fontSize: 13, color: "var(--muted)", textAlign: "center" }}>
          {t.recipesLoading}
        </p>
      ) : recipes && recipes.length === 0 ? (
        <p
          className="text-center px-4"
          style={{ marginTop: 24, fontSize: 13, color: "var(--muted)" }}
        >
          {t.recipesEmpty}
        </p>
      ) : (
        <div style={{ marginTop: 20 }}>
          {recipes?.map((recipe, idx) => {
            const conf = confLevelFromScore(recipe.confidence);
            const missCount = recipe.missing.length;
            const isExpanded = expandedIdx === idx;
            return (
              <article
                key={`${recipe.name}-${idx}`}
                style={{
                  padding: "18px 0",
                  borderTop: "1px solid var(--nav-border)",
                }}
              >
                <button
                  type="button"
                  onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                  className="text-left w-full"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                  aria-expanded={isExpanded}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      marginBottom: 8,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 4,
                          flexWrap: "wrap",
                        }}
                      >
                        <Conf level={conf} />
                        {recipe.mealType ? (
                          <MonoMeta>{MEAL_TYPE_LABELS[recipe.mealType].toUpperCase()}</MonoMeta>
                        ) : null}
                        {missCount === 0 ? (
                          <span
                            style={{
                              fontFamily: "var(--font-mono)",
                              fontSize: 9,
                              padding: "2px 6px",
                              borderRadius: 3,
                              background: "var(--ok-bg)",
                              color: "var(--ok-fg)",
                              letterSpacing: "0.06em",
                              fontWeight: 700,
                            }}
                          >
                            {t.recipesHasAll}
                          </span>
                        ) : (
                          <span
                            style={{
                              fontFamily: "var(--font-mono)",
                              fontSize: 9,
                              padding: "2px 6px",
                              borderRadius: 3,
                              background: "var(--bad-bg)",
                              color: "var(--bad-fg)",
                              letterSpacing: "0.06em",
                              fontWeight: 700,
                            }}
                          >
                            {t.recipesMissing(missCount)}
                          </span>
                        )}
                      </div>
                      <h3
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: "var(--ink)",
                          lineHeight: 1.25,
                        }}
                      >
                        {recipe.name}
                      </h3>
                      <div style={{ marginTop: 3 }}>
                        <MonoMeta>
                          {recipe.estimatedProteinG !== null
                            ? `B${Math.round(recipe.estimatedProteinG)}`
                            : ""}
                          {recipe.estimatedFatG !== null
                            ? ` T${Math.round(recipe.estimatedFatG)}`
                            : ""}
                          {recipe.estimatedCarbsG !== null
                            ? ` W${Math.round(recipe.estimatedCarbsG)}`
                            : ""}
                        </MonoMeta>
                      </div>
                    </div>
                    <div
                      className="tabular"
                      style={{
                        fontFamily: "var(--font-display)",
                        fontSize: 26,
                        fontWeight: 800,
                        color: "var(--ink)",
                        letterSpacing: "-0.03em",
                      }}
                    >
                      {recipe.estimatedKcal}
                    </div>
                  </div>
                </button>

                {isExpanded ? (
                  <div style={{ marginTop: 8 }}>
                    <p
                      style={{
                        fontSize: 13,
                        color: "var(--ink-2)",
                        lineHeight: 1.5,
                        marginBottom: 12,
                      }}
                    >
                      {recipe.description}
                    </p>

                    {recipe.uses.length > 0 ? (
                      <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                        <div
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: 10,
                            color: "var(--ok-fg)",
                            fontWeight: 700,
                            letterSpacing: "0.1em",
                            flex: "0 0 60px",
                          }}
                        >
                          {t.recipesHaveLabel}
                        </div>
                        <div style={{ fontSize: 13, color: "var(--ink-2)", flex: 1 }}>
                          {recipe.uses
                            .map((u) =>
                              u.quantity !== null
                                ? `${u.name} (${u.quantity}${u.unit ? ` ${u.unit}` : ""})`
                                : u.name,
                            )
                            .join(" · ")}
                        </div>
                      </div>
                    ) : null}
                    {recipe.missing.length > 0 ? (
                      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                        <div
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: 10,
                            color: "var(--bad-fg)",
                            fontWeight: 700,
                            letterSpacing: "0.1em",
                            flex: "0 0 60px",
                          }}
                        >
                          {t.recipesMissingLabel}
                        </div>
                        <div style={{ fontSize: 13, color: "var(--ink-2)", flex: 1 }}>
                          {recipe.missing
                            .map((m) =>
                              m.quantity !== null
                                ? `${m.name} (${m.quantity}${m.unit ? ` ${m.unit}` : ""})`
                                : m.name,
                            )
                            .join(" · ")}{" "}
                          <button
                            type="button"
                            onClick={() => addMissingToShopping(recipe, idx)}
                            disabled={addingShopIdx === idx || addedShopIdx.has(idx)}
                            className="disabled:opacity-60"
                            style={{
                              color: "var(--accent)",
                              fontWeight: 700,
                              fontSize: 13,
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: 0,
                            }}
                          >
                            {addedShopIdx.has(idx)
                              ? t.recipesAddedToList
                              : addingShopIdx === idx
                                ? t.recipesAddingToList
                                : t.recipesAddToList}
                          </button>
                        </div>
                      </div>
                    ) : null}

                    <div style={{ marginTop: 10 }}>
                      <Eyebrow>{t.recipesHowTo}</Eyebrow>
                      <p
                        style={{
                          marginTop: 6,
                          fontSize: 13,
                          color: "var(--ink-2)",
                          lineHeight: 1.6,
                        }}
                      >
                        {recipe.instructions}
                      </p>
                    </div>

                    <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => quickLog(recipe, idx)}
                        disabled={loggingIdx === idx}
                        className="uppercase disabled:opacity-50"
                        style={{
                          flex: 1,
                          padding: "12px 16px",
                          borderRadius: 10,
                          background: "var(--ink)",
                          color: "var(--bg)",
                          border: "none",
                          fontFamily: "var(--font-mono)",
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                        }}
                      >
                        {loggingIdx === idx ? t.saving : t.recipesLogIt}
                      </button>
                      <button
                        type="button"
                        onClick={() => editBeforeLog(recipe)}
                        className="uppercase"
                        style={{
                          padding: "12px 16px",
                          borderRadius: 10,
                          background: "transparent",
                          color: "var(--ink)",
                          boxShadow: "inset 0 0 0 1px var(--nav-border)",
                          border: "none",
                          fontFamily: "var(--font-mono)",
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                        }}
                      >
                        {t.recipesEdit}
                      </button>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={() => load(mealTypeFilter === "all" ? undefined : mealTypeFilter)}
        disabled={loading}
        className="w-full uppercase disabled:opacity-50"
        style={{
          marginTop: 18,
          padding: "14px 20px",
          borderRadius: 12,
          background: "transparent",
          color: "var(--ink)",
          boxShadow: "inset 0 0 0 1px var(--nav-border)",
          border: "none",
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.08em",
        }}
      >
        {loading ? "…" : t.recipesOther}
      </button>

      <AiFooter />
    </ScreenShell>
  );
}
